import { useEffect, useState } from "react";
import {
  X,
  Barcode,
  Sparkles,
  ShoppingBag,
  AlertTriangle,
  RefreshCcw,
} from "lucide-react";
import { toast } from "sonner";

import { api, formatCurrency, formatDate } from "@/lib/api";

/**
 * Unified Mint Details dialog for pending_data / ready_to_mint / mint_failed records.
 * - Save: PUT details, recalc readiness
 * - Save & Mint: PUT details then POST /mint_chain
 * - Mint NFT: POST /mint_chain (ready_to_mint)
 * - Retry Mint: POST /mint_chain (mint_failed, after optional edit)
 * Seed records (source==="seed") route through legacy /mint mock endpoint.
 */
export default function MintDetailsDialog({ open, receipt, onClose, onUpdated }) {
  const [serial, setSerial] = useState("");
  const [email, setEmail] = useState("");
  const [wallet, setWallet] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && receipt) {
      setSerial(receipt.serial_number || "");
      setEmail(receipt.customer_email || "");
      setWallet(receipt.recipient_wallet || "");
      setNotes(receipt.mint_notes || "");
      setBusy(false);
      setError(
        receipt.status === "mint_failed" && receipt.mint_error
          ? receipt.mint_error
          : "",
      );
    }
  }, [open, receipt?.id]);

  if (!open || !receipt) return null;

  const isSeed = receipt.source === "seed";
  const status = receipt.status;
  const item = receipt.items?.[0];

  const titleForStatus = {
    pending_data: "Complete Record",
    ready_to_mint: "Issue Certificate",
    mint_failed: "Retry Issuance",
  };
  const subForStatus = {
    pending_data: "Fill in the required details, then save or issue.",
    ready_to_mint: "All required fields are present. Issue on-chain now.",
    mint_failed: "Previous issuance attempt failed. Review and retry.",
  };

  const saveDetails = async () => {
    const { data } = await api.post(`/receipts/${receipt.id}/details`, {
      serial_number: serial,
      recipient_email: email,
      recipient_wallet: wallet,
      notes,
    });
    return data;
  };

  const mintOnChain = async () => {
    // Seed records → legacy mock mint
    const url = isSeed
      ? `/receipts/${receipt.id}/mint`
      : `/receipts/${receipt.id}/mint_chain`;
    const { data } = await api.post(url);
    return data;
  };

  const handleSave = async () => {
    setBusy(true);
    setError("");
    try {
      const updated = await saveDetails();
      toast.success(
        updated.status === "ready_to_mint" ? "Saved · ready to mint" : "Saved",
      );
      onUpdated?.(updated);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveAndMint = async () => {
    if (!serial.trim()) {
      setError("Serial Number is required");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await saveDetails();
      const minted = await mintOnChain();
      toast.success(`Certificate issued · ${minted.nft_id || "on-chain"}`);
      onUpdated?.(minted);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Issuance failed. Please retry.");
    } finally {
      setBusy(false);
    }
  };

  const handleMint = async () => {
    setBusy(true);
    setError("");
    try {
      const minted = await mintOnChain();
      toast.success(`Certificate issued · ${minted.nft_id || "on-chain"}`);
      onUpdated?.(minted);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Issuance failed. Please retry.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="mint-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-[2px] overflow-y-auto"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden my-8"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <span className="h-9 w-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
              {status === "mint_failed" ? <RefreshCcw size={16} /> : <Barcode size={16} />}
            </span>
            <div>
              <div className="font-display font-semibold text-gray-900">
                {titleForStatus[status] || "Issue Certificate"}
              </div>
              <div className="text-[11px] tracking-wider uppercase text-gray-500">
                {subForStatus[status] || "Complete and issue this record."}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="mint-dialog-close"
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-900 hover:bg-gray-100"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <section>
            <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-gray-500 mb-2">
              Order Summary
            </div>
            <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3">
              <div className="flex items-start gap-3">
                <ShoppingBag size={16} className="text-gray-400 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 truncate">
                    {item?.name || "Item"}
                  </div>
                  <div className="mt-0.5 grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-gray-600">
                    <div><span className="text-gray-400">Store:</span> {receipt.store_name || "—"}</div>
                    <div><span className="text-gray-400">SKU:</span> {item?.sku || "—"}</div>
                    <div><span className="text-gray-400">Order #:</span> {receipt.source_order_id || "—"}</div>
                    <div><span className="text-gray-400">Unit:</span> {receipt.source_unit_index ?? "—"}</div>
                    <div><span className="text-gray-400">Date:</span> {formatDate(receipt.purchase_date)}</div>
                    <div><span className="text-gray-400">Price:</span> {formatCurrency(receipt.total, receipt.currency)}</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-gray-500">
              Required Issuance Details
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Serial Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                data-testid="mint-serial"
                value={serial}
                onChange={(e) => setSerial(e.target.value)}
                placeholder={isSeed ? "Demo · any value" : "e.g. SN-OMEGA-001"}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Recipient Email
              </label>
              <input
                type="email"
                data-testid="mint-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="customer@example.com"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF]"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Recipient Wallet <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  data-testid="mint-wallet"
                  value={wallet}
                  onChange={(e) => setWallet(e.target.value)}
                  placeholder="0x…"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Notes <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  data-testid="mint-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Internal note"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF]"
                />
              </div>
            </div>
          </section>

          {error && (
            <div
              data-testid="mint-error"
              className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
            >
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/60 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="mint-cancel"
            className="text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md"
          >
            Cancel
          </button>
          {status === "pending_data" && (
            <>
              <button
                type="button"
                onClick={handleSave}
                disabled={busy}
                data-testid="mint-save"
                className="text-sm font-semibold text-gray-900 bg-white border border-gray-300 px-4 py-2 rounded-md hover:border-[#0055FF] hover:text-[#0055FF] transition disabled:opacity-60"
              >
                Save
              </button>
              <button
                type="button"
                onClick={handleSaveAndMint}
                disabled={busy}
                data-testid="mint-save-and-mint"
                className="inline-flex items-center gap-1.5 bg-[#0055FF] text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#0044CC] transition disabled:opacity-60"
              >
                <Sparkles size={14} />
                {busy ? "Working…" : "Save & Issue"}
              </button>
            </>
          )}
          {status === "ready_to_mint" && (
            <button
              type="button"
              onClick={handleMint}
              disabled={busy}
              data-testid="mint-now"
              className="inline-flex items-center gap-1.5 bg-[#0055FF] text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#0044CC] transition disabled:opacity-60"
            >
              <Sparkles size={14} />
              {busy ? "Issuing…" : "Issue Certificate"}
            </button>
          )}
          {status === "mint_failed" && (
            <>
              <button
                type="button"
                onClick={handleSave}
                disabled={busy}
                data-testid="mint-save-changes"
                className="text-sm font-semibold text-gray-900 bg-white border border-gray-300 px-4 py-2 rounded-md hover:border-[#0055FF] hover:text-[#0055FF] transition disabled:opacity-60"
              >
                Save Changes
              </button>
              <button
                type="button"
                onClick={handleSaveAndMint}
                disabled={busy}
                data-testid="mint-retry"
                className="inline-flex items-center gap-1.5 bg-[#0055FF] text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#0044CC] transition disabled:opacity-60"
              >
                <RefreshCcw size={14} />
                {busy ? "Retrying…" : "Retry Issuance"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
